import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getBlobApiErrorMessage } from '../api/client';
import { formatCalendarDate, formatInstant, formatInstantTenantDay, todayCalendarDate } from '../lib/calendar-date';
import { readTicketDate } from '../lib/ticket-date';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import { fmtCurrency, fmtUnitRate, parseMoney, fmtUnit, fmtLineItemType } from '../lib/display-labels';
import { ticketPdfNeedsPreview } from '../lib/ticket-status';
import { claimBlobTab, showBlobInTab, saveBlobAs, POPUP_BLOCKED_MESSAGE } from '../lib/blob-tab';
import { unwrap } from '../lib/api-envelope';
import { humanizeLabel } from '../lib/reports';
import { useAuth } from '../contexts/AuthContext';
import { useInvoicePermissions } from '../hooks/useInvoicePermissions';
import { downloadInvoiceCsv } from '../lib/invoice-csv-export';
import { useQboReadiness, qboPushGate, QBO_READINESS_KEY } from '../hooks/useQboReadiness';
import { readQboPushRefusal, qboPushSuccessMessage, qboMappingTabHref, type QboPushRefusal } from '../lib/qbo-errors';
import { QboPushRefusalNotice, QboPushOffReason } from '../components/invoices/QboPushNotices';
import type { QboPushResult } from '../types/qbo-mapping';
import { getRoleLabel } from '../lib/roles';
import DatePicker from '../components/DatePicker';
import { SuppressedRecipientNotice, suppressedRefusalOf, type SuppressedRefusal } from '../components/SuppressedRecipientNotice';
import { PageHeaderBand } from '../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../components/ui/LoadFailureNotice';
import {
  LOAD_FAILURE_QUERY_OPTIONS,
  queryFailureError,
  queryLoadFailure,
} from '../lib/load-failure';
import { displayValue } from '../features/fieldiq-handoff/contracts';
import {
  FileText,
  Send,
  Download,
  Pencil,
  DollarSign,
  Ban,
  ChevronDown,
  ChevronUp,
  Loader2,
  CreditCard,
  ExternalLink,
  Clock,
  User,
  Users,
  X,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Wallet,
  Receipt,
} from 'lucide-react';
import { Tooltip } from '../components/ui/tooltip';

// ─── Types ──────────────────────────────────────────────────────────────────

interface LineItem {
  id: string;
  ticketNumber?: string;
  date?: string;
  type?: string;
  description?: string;
  quantity: number;
  unit?: string;
  rate: number;
  amount: number;
  isDiscount?: boolean;
}

interface Payment {
  id: string;
  date: string;
  amount: number;
  method?: string;
  reference?: string;
}

interface Ticket {
  id: string;
  ticketNumber: string;
}

/**
 * ONE AUDIT ROW, TYPED TO THE PAYLOAD THE API ACTUALLY SENDS.
 *
 * `GET /invoices/:id/audit-log` hands back the raw joined row — snake_case,
 * with the user split across three columns. This interface declared
 * `createdAt` and `userName`, neither of which has ever been a key on it, so
 * every row on this panel rendered **"Invalid Date"** and no row has ever named
 * who did the thing. TypeScript could not see it: the interface declared the
 * wrong fields too, which is the #1 recurring bug in this repo written out in
 * eight lines.
 *
 * Verified against a live response on Smith, 2026-09-22, not assumed:
 *   id, company_id, invoice_id, user_id, action, old_values, new_values,
 *   description, created_at, first_name, last_name, email
 *
 * `created_at` arrives in the POSTGRES shape — '2026-09-04 00:56:18.511471+00'
 * — a space instead of the T and a two-digit offset. V8 parses that leniently
 * and JavaScriptCore rejects it, so even with the key corrected a bare
 * `new Date()` here would read correctly on desktop and degrade to nothing on
 * the iOS build. `parseInstantDate` normalises the shape first; that is the
 * whole reason to route through it rather than construct a Date inline.
 *
 * The camelCase spellings are kept as fallbacks rather than deleted — reads
 * tolerate both, per CLAUDE.md's serialization asymmetry — but they are the
 * SECOND rung now, not the only one.
 */
interface AuditEntry {
  id: string;
  action: string;
  description?: string;
  created_at?: string;
  createdAt?: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  userName?: string;
}

/** Who did it, or null — never a guess, and never the empty string. */
function auditActor(entry: AuditEntry): string | null {
  const name = [entry.first_name, entry.last_name].filter(Boolean).join(' ').trim();
  return name || entry.userName || entry.email || null;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  status: string;
  customerName?: string;
  /**
   * THE RECIPIENT, UNDER THE KEY THE WRITE ALSO USES.
   *
   * `GET /invoices/:id` emitted this address as `customerEmail` and nothing
   * else, while `PUT /invoices/:id` has only ever accepted
   * `customerContactEmail` — one column, two spellings, one on each side of
   * the wire. api#798 made the read emit BOTH, the new key alongside a
   * deprecated `customerEmail` alias kept solely so this screen could move,
   * and that alias is dropped from the API in the PR after this one. So this
   * declares the write spelling only: adding the old one back as a fallback
   * would silently keep working after the API stops sending it, which is the
   * state that made the rename need a PR of its own.
   */
  customerContactEmail?: string;
  /**
   * WHICH `customers` column supplied the address — 'accounting_email' or
   * 'main_contact_email'. Null on any invoice created before API 0392, and on
   * a customer that had neither address.
   *
   * NOT RENAMED, AND THAT IS NOT AN OVERSIGHT. api#798 deprecated
   * `customerEmail` and left `customerEmailSource` exactly as it is — it is
   * the read's own key, with no write twin to agree with. Renaming it to match
   * its neighbour would bind to a key the API has never sent, and the cell
   * would fall to 'Source not recorded' on every invoice.
   *
   * Typed to the payload, per the #1 recurring bug in this repo: a null here
   * renders as UNKNOWN and must never fall back to naming either column. An
   * invoice aimed at a switchboard and one aimed at accounts payable are
   * otherwise the same line of text, which is how 16 TNT drafts sat pointed at
   * info@cenovus.com for months.
   */
  customerEmailSource?: 'accounting_email' | 'main_contact_email' | null;
  subtotal: number;
  discountTotal?: number;
  taxLines?: { label: string; rate: number; amount: number }[];
  taxTotal?: number;
  total: number;
  amountPaid: number;
  balanceDue: number;
  lineItems: LineItem[];
  payments: Payment[];
  tickets: Ticket[];
  createdAt: string;
  dueDate?: string;
  /**
   * The QuickBooks invoice this one was pushed as; `null` means never pushed.
   * Sent by `GET /invoices/:id` since api#896, under the same key and value as
   * the list. "Pushed" is keyed on THIS, not on `qboFirstPushedAt`: invoices
   * pushed before API 0445 carry an id with a NULL first-push time.
   */
  qboInvoiceId?: string | null;
}

// ─── Status badge ───────────────────────────────────────────────────────────

/**
 * THE STATUS BADGE — saturated fill, dark ink, and an em dash for anything
 * outside the map. Same shape as TicketDetail's (fe#734); the labels are this
 * document's own.
 *
 * Five signal hues and no sixth, grouped by what the office has to do:
 *
 *   slate   nothing is in play — draft, and void, which is over
 *   amber   the OFFICE must act — finalized (priced, not yet sent), overdue
 *   blue    out with the customer, waiting — sent
 *   green   settled — paid
 *
 * `text-fi-signal-ink` on a `bg-fi-signal-*` fill: the signal hues are declared
 * once and do not follow the theme, so one dark ink holds on all of them in
 * both halves. This replaced a pale-wash-plus-bright-ink shape whose every
 * spelling was a raw Tailwind shade — a 400-weight ink on a white panel in
 * light mode, which is the failure CLAUDE.md records for `/payroll/approvals`.
 * (Described rather than spelled: the Tailwind scanner reads a comment as
 * source, so naming the retired classes here would mint them again.)
 *
 * A VALUE OUTSIDE THIS MAP READS `—` ON SLATE. The old map fell through to a
 * grey pill carrying the RAW status string, which on an invoice means the
 * screen printing a word it could not classify as though it were a state.
 *
 * `overdue` is amber and not red deliberately: red on this register means the
 * document came back wrong. An overdue invoice is correct and unpaid, which is
 * the office's job, not the customer's rejection.
 */
const STATUS_BADGE: Record<string, { label: string; fill: string }> = {
  draft:     { label: 'Draft',    fill: 'bg-fi-signal-slate' },
  finalized: { label: 'Saved',    fill: 'bg-fi-signal-amber' },
  sent:      { label: 'Sent',     fill: 'bg-fi-signal-blue' },
  paid:      { label: 'Paid',     fill: 'bg-fi-signal-green' },
  overdue:   { label: 'Overdue',  fill: 'bg-fi-signal-amber' },
  void:      { label: 'Void',     fill: 'bg-fi-signal-slate' },
};

const UNKNOWN_STATUS = { label: '\u2014', fill: 'bg-fi-signal-slate' };

const statusSkin = (status: string | null | undefined) =>
  (status && STATUS_BADGE[status]) || UNKNOWN_STATUS;

const STATUS_PILL =
  'inline-flex w-fit items-center gap-1 whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink';

// ─── Body action skins ──────────────────────────────────────────────────────

/**
 * THE ACTIONS SIT IN THE BODY, NOT ON THE BAND, so these are spelled in
 * THEME-FOLLOWING ink tokens — the exact opposite of the band's constant white
 * opacities. `text-fieldiq-text` and `text-fieldiq-accent` are both remapped
 * under the light attribute (the accent to amber-700, 5.02 on the white card);
 * the raw 400-weight shades these replaced are not remapped at all and read
 * around 2:1 on a white panel.
 *
 * AND THE HOVER IS THE ACCENT, NEVER A WHITE-INK HOVER. theme.css rewrites the
 * exact white-ink selector to the heading colour under the light attribute and
 * deliberately does NOT rewrite its hover variant — so a rest-state ink paired
 * with a white hover puts literal #fff on a white card the moment a pointer
 * lands on it, and the control vanishes while being pointed at. Five sites on
 * this screen did that.
 *
 * (The retired class is DESCRIBED rather than spelled. The Tailwind scanner has
 * no syntax and reads a comment as source, so the comment explaining a removal
 * is itself a call site — twice in this repo, the second time in a PR whose own
 * body cited the rule.)
 */
const ACTION_PRIMARY = 'fi-action fi-action--standard fi-action--primary';
const ACTION_SECONDARY =
  'fi-action fi-action--standard rounded-fi-control border border-fieldiq-border bg-fieldiq-card text-fieldiq-text transition-colors hover:text-fieldiq-accent disabled:opacity-50';
/** Destructive, in the body idiom TicketDetail uses: a danger EDGE, neutral ink. */
const ACTION_DANGER =
  'fi-action fi-action--standard rounded-fi-control border border-fieldiq-danger/40 bg-fieldiq-card text-fieldiq-text transition-colors hover:bg-fieldiq-danger/10 disabled:opacity-50';

// ─── Money formatter ────────────────────────────────────────────────────────

/**
 * Money on this document renders its sign OUTSIDE the symbol — '-$42.00', not
 * fmtCurrency's own '$-42.00'. That is the only local behaviour here, so only
 * that is local: the absent/real-zero decision and the digits come from the
 * shared helper. The body used to be `Number(value) || 0`, which rendered a
 * credit the server never sent as $0.00.
 */
function formatMoney(value: any): string {
  const n = parseMoney(value);
  return n === null ? '\u2014' : fmtCurrency(Math.abs(n));
}

/** A line's unit rate: as formatMoney, with the decimals the rate carries (fmtUnitRate). */
function formatRate(value: any): string {
  const n = parseMoney(value);
  return n === null ? '\u2014' : fmtUnitRate(Math.abs(n));
}

function formatMoneyWithSign(value: any): string {
  const n = parseMoney(value);
  if (n === null) return '\u2014';
  return n < 0 ? `-${fmtCurrency(Math.abs(n))}` : fmtCurrency(n);
}

// ─── The band, and the panel the body is built from ─────────────────────────

/**
 * THE BAND, IN ONE PLACE, because this page has four states and every one of
 * them has to wear it.
 *
 * The eyebrow is constant and the title is the invoice's own identity — this
 * is a detail page, so the band names the record rather than the register. It
 * carries the back arrow (a plain `ArrowLeft` drawn by the band itself, no
 * visible word) and NO description: a description line has never been measured
 * against the hero circle's clearance, and neither reference page uses one.
 *
 * WHERE THE REST OF THE IDENTITY WENT. Status, customer and due date sit in an
 * identity row in the BODY, directly under the band — which is where
 * TicketDetail (fe#734, the reference for this page) puts them. They are not on
 * the band because the only slots that would take them are a description line
 * and the stat strip, and the circle's clearance was swept against stat TEXT
 * only. A customer name is unbounded; "Cenovus Energy Inc." and a 60-character
 * numbered-company name are the same field.
 *
 * `stats` is optional and is OMITTED — not zeroed — while the invoice has not
 * arrived. A strip of em dashes on a page that is still loading states that the
 * invoice has no total.
 */
function InvoiceBand({
  title,
  onBack,
  stats,
}: {
  title: string;
  onBack: () => void;
  stats?: React.ComponentProps<typeof PageHeaderBand>['stats'];
}) {
  return (
    <PageHeaderBand
      eyebrow="Accounts receivable"
      title={title}
      onBack={onBack}
      backLabel="Back to Invoices"
      stats={stats}
    />
  );
}

/**
 * A record panel, in the register idiom: one amber 4px left edge on the PANEL,
 * an eyebrow and an `<h2>`, and the rows inside it.
 *
 * The sections below used a heading floating inside an unrelated rounded box,
 * so a reader scrolling the page met six boxes with nothing tying them to the
 * register the rest of the app uses. Nothing about a row changed to get this.
 */
function RecordPanel({
  eyebrow,
  title,
  count,
  children,
}: {
  eyebrow: string;
  title: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card">
      {/* A div, not a <header>: inside a <section> the element still maps to
          the banner role in some accessibility trees, and six banners on a page
          is not what a register header is. */}
      <div className="border-b border-fieldiq-border px-4 py-3">
        <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">{eyebrow}</p>
        <h2 className="mt-1 text-base font-extrabold tracking-tight text-white">
          {title}
          {count !== undefined && <span className="ml-2 font-bold text-fieldiq-muted">{count}</span>}
        </h2>
      </div>
      {children}
    </section>
  );
}

// ─── Toast component ────────────────────────────────────────────────────────

/**
 * `tone` exists because a REFUSAL must not render as a confirmation. This
 * screen's toast was neutral and unlabelled, so "Failed to update recipient"
 * and "Recipient updated" arrived looking identical — the #421/#452 shape,
 * where a permission refusal was shown under a green checkmark for a month.
 * Same family as "an absent value renders as —, never 0": a wrong signal that
 * looks deliberate is worse than a missing one.
 */
function Toast({ message, tone = 'success', onClose }: { message: string; tone?: 'success' | 'error'; onClose: () => void }) {
  React.useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const isError = tone === 'error';
  return (
    <div
      role="status"
      /* The error tone is the BADGE idiom — a saturated signal fill with the one
         dark ink that holds on all five hues in both themes. It was a raw
         600-weight red with white text, which is a hue this app's palette does
         not declare and which no theme switch reaches. */
      className={`fixed bottom-[var(--fab-bottom)] left-1/2 z-50 flex -translate-x-1/2 animate-fade-in items-center gap-2 rounded-fi-panel px-5 py-3 text-sm shadow-lg ${
        isError
          ? 'border border-black/20 bg-fi-signal-red font-semibold text-fi-signal-ink'
          : 'border border-fieldiq-border bg-fieldiq-card text-white'
      }`}
    >
      {isError && <AlertCircle size={16} />}
      <span>{message}</span>
    </div>
  );
}

// ─── Record Payment Modal ───────────────────────────────────────────────────

interface RecordPaymentModalProps {
  invoiceId: string;
  balanceDue: number;
  onClose: () => void;
  onSuccess: () => void;
  onError: (msg: string) => void;
}

function RecordPaymentModal({ invoiceId, balanceDue, onClose, onSuccess, onError }: RecordPaymentModalProps) {
  const queryClient = useQueryClient();
  const timeZone = useTenantTimeZone();
  const [amount, setAmount] = useState(balanceDue.toFixed(2));
  const [method, setMethod] = useState('cheque');
  const [reference, setReference] = useState('');
  // The TENANT's today as the input seed; '' (an empty picker) only when the
  // zone is unknown, never a guessed day.
  const [date, setDate] = useState(todayCalendarDate(timeZone) ?? '');
  const [bankAccount, setBankAccount] = useState('');
  const [notes, setNotes] = useState('');

  const mutation = useMutation({
    mutationFn: (data: { amount: number; method: string; reference: string; date: string }) =>
      api.invoices.recordPayment(invoiceId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      onSuccess();
      onClose();
    },
    onError: (err: any) => {
      onError(err?.response?.data?.error || 'Failed to record payment');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parseFloat(amount);
    if (isNaN(parsed) || parsed <= 0) {
      onError('Enter a valid amount');
      return;
    }
    mutation.mutate({ amount: parsed, method, reference, date, bankAccount: bankAccount || undefined, notes: notes || undefined } as any);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl w-full max-w-md p-5 space-y-4 animate-fade-in">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Record Payment</h3>
          <button onClick={onClose} className="text-fieldiq-text hover:text-fieldiq-accent">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Amount</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white focus:outline-none focus:border-fieldiq-accent"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Payment Method</label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white focus:outline-none focus:border-fieldiq-accent"
            >
              <option value="cheque">Cheque</option>
              <option value="e_transfer">E-Transfer</option>
              <option value="direct_deposit">Direct Deposit</option>
              <option value="eft">EFT / Wire</option>
              <option value="credit_card">Credit Card</option>
              <option value="cash">Cash</option>
              <option value="stripe">Stripe</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-fieldiq-text mb-1">
              {method === 'cheque' ? 'Cheque #' : method === 'e_transfer' ? 'E-Transfer Reference' : 'Reference #'}
            </label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder={method === 'cheque' ? 'e.g. 10452' : method === 'e_transfer' ? 'Sender name or ref' : 'Optional'}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white placeholder-fieldiq-text/50 focus:outline-none focus:border-fieldiq-accent"
            />
          </div>

          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Bank Account</label>
            <input
              type="text"
              value={bankAccount}
              onChange={(e) => setBankAccount(e.target.value)}
              placeholder="e.g. Operating, Trust, Savings"
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white placeholder-fieldiq-text/50 focus:outline-none focus:border-fieldiq-accent"
            />
          </div>

          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Date</label>
            <DatePicker value={date} onChange={setDate} />
          </div>

          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Notes</label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white placeholder-fieldiq-text/50 focus:outline-none focus:border-fieldiq-accent"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-lg border border-fieldiq-border text-fieldiq-text hover:text-fieldiq-accent transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1 py-2.5 rounded-lg bg-fieldiq-accent text-black font-semibold hover:bg-fieldiq-accent/90 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {mutation.isPending && <Loader2 size={16} className="animate-spin" />}
              Record Payment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * The DEMO_INVOICES map that stood here is gone.
 *
 * Seven fabricated invoices keyed 'demo-1'..'demo-7', attributed to five
 * named oil and gas operators, were served from memory whenever the
 * :invoiceId began with 'demo-'. (The names are not repeated here: they must
 * not survive in the built bundle, source maps included.) They were reachable
 * because InvoicesScreen substituted the matching list for any tenant whose
 * real invoice list came back empty, so a live pilot with no invoices could
 * open one and read another company's line items, rates and balance.
 *
 * A demo invoice also had its own client-side print path, which is likewise
 * gone: the real one is api.invoices.getPdf, server-rendered.
 */

// ─── Main Component ─────────────────────────────────────────────────────────

export default function InvoiceDetailScreen() {
  const timeZone = useTenantTimeZone();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Read only to name the caller's role in a refusal notice.
  const { user } = useAuth();
  const perms = useInvoicePermissions();
  const [csvDownloading, setCsvDownloading] = useState(false);

  const [toast, setToast] = useState<string | null>(null);
  const [toastTone, setToastTone] = useState<'success' | 'error'>('success');
  // A push refusal the office can read (API #887): already pushed, in
  // progress, a conflicting invoice number, or mapping still to do.
  const [qboRefusal, setQboRefusal] = useState<QboPushRefusal | null>(null);
  // The QuickBooks id learned from THIS screen's push or already-pushed
  // refusal, so the badge appears without waiting for the invoice to refetch.
  const [pushedQboId, setPushedQboId] = useState<string | null>(null);
  const qboReadiness = useQboReadiness();
  const pushGate = qboPushGate(qboReadiness);
  const showToast = (message: string, tone: 'success' | 'error' = 'success') => { setToastTone(tone); setToast(message); };
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSendEmail, setShowSendEmail] = useState(false);
  const [sendEmailAddress, setSendEmailAddress] = useState('');
  // The recipient stored ON the invoice, editable in place. Distinct from
  // `sendEmailAddress`, which is a one-off override for a single send and is
  // NOT written back to the document.
  const [editingRecipient, setEditingRecipient] = useState(false);
  const [recipientDraft, setRecipientDraft] = useState('');
  const [savingRecipient, setSavingRecipient] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  // A 409 `recipient_suppressed` from the send door. Held in state, not a
  // toast, because the office needs the dated sentence in front of them while
  // they correct the address — and needs the "send anyway" escape hatch, which
  // re-posts with `force: true`. Remembers WHICH address was refused so the
  // forced send goes to the same one.
  const [suppressed, setSuppressed] = useState<{ refusal: SuppressedRefusal; email?: string } | null>(null);

  // ─── Fetch invoice ─────────────────────────────────────────────────────

  const invoiceQuery = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: async () => {
      const res = await api.invoices.get(invoiceId!);
      // `data: null` means the server has no invoice for this id. `r.data.data
      // as Invoice` typed the ENVELOPE as an Invoice whenever data was null and
      // every field below then read `undefined` behind an Invoice type — the
      // `res.data?.data || res.data` family, in its `as` costume.
      return unwrap<Invoice>(res.data) ?? null;
    },
    enabled: !!invoiceId,
    // Retry anything worth retrying and NOT a refusal, and keep the error where
    // the screen can find it. Without this a 500 leaves the query `paused` with
    // `error: null`, `data: undefined` and `isLoading: false` — which the old
    // `isError || !invoice` branch read as "Invoice not found", so a dropped
    // connection and a 403 both rendered as a fact about the row.
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

  const invoice = invoiceQuery.data;
  const isLoading = invoiceQuery.isLoading;
  // Read off the QUERY, not off `error` alone: a query between retries carries
  // its failure in `failureReason` and nowhere else.
  const invoiceFailure = queryLoadFailure(invoiceQuery);

  // ─── Fetch audit log (lazy) ─────────────────────────────────────────────

  const { data: auditLog, isLoading: auditLoading } = useQuery({
    queryKey: ['invoice', invoiceId, 'audit-log'],
    queryFn: () => api.invoices.auditLog(invoiceId!).then((r) => (r.data.data ?? r.data) as AuditEntry[]),
    enabled: !!invoiceId && auditOpen,
  });

  // ─── Mutations ──────────────────────────────────────────────────────────

  const sendMutation = useMutation({
    mutationFn: (data: { email?: string; force?: boolean }) => api.invoices.send(invoiceId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      setSuppressed(null);
      setShowSendEmail(false);
      setToast('Invoice sent successfully');
    },
    onError: (err: any, data) => {
      const refusal = suppressedRefusalOf(err);
      if (refusal) {
        // Refused before anything was stamped: the invoice is still where it
        // was. Show the sentence and the escape hatch instead of a toast.
        setSuppressed({ refusal, email: data?.email });
        return;
      }
      setToast(err?.response?.data?.error || 'Failed to send invoice');
    },
  });

  const voidMutation = useMutation({
    mutationFn: () => api.invoices.void(invoiceId!),
    onSuccess: () => {
      setToast('Invoice voided');
      navigate(-1);
    },
    onError: (err: any) => {
      setToast(err?.response?.data?.error || 'Failed to void invoice');
    },
  });

  const qboPushMutation = useMutation({
    mutationFn: () => api.quickbooks.pushInvoice(invoiceId!),
    onMutate: () => setQboRefusal(null),
    onSuccess: (res) => {
      const result = unwrap<QboPushResult>(res.data);
      if (result?.qboInvoiceId) setPushedQboId(result.qboInvoiceId);
      // `adopted` linked an invoice QuickBooks already held — it created
      // nothing, so it must not read "Pushed".
      showToast(qboPushSuccessMessage(result));
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
    },
    onError: (err: any) => {
      // A refusal the office can act on is a PANEL, never a toast: a mapping
      // gap routes to the tab that blocked it, and "already in QuickBooks" is
      // information, not an error — see QboPushNotices for the tones.
      const refusal = readQboPushRefusal(err);
      if (refusal) {
        setQboRefusal(refusal);
        if (refusal.kind === 'already_pushed') {
          if (refusal.qboInvoiceId) setPushedQboId(refusal.qboInvoiceId);
          queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
        }
        if (refusal.kind === 'in_progress') {
          setTimeout(() => queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] }), 15_000);
        }
        // The button was enabled on a readiness read the server now disagrees
        // with, so that read is stale.
        if (refusal.kind === 'not_ready') {
          queryClient.invalidateQueries({ queryKey: QBO_READINESS_KEY });
        }
        return;
      }
      showToast(err?.response?.data?.error || 'Failed to push to QuickBooks', 'error');
    },
  });

  // ─── Actions ────────────────────────────────────────────────────────────

  /**
   * THE TAB IS CLAIMED ON THE CLICK, BEFORE THE AWAIT — NOT AFTER IT.
   *
   * This used to `await` the PDF and then call `window.open`, which is past
   * Chrome's five-second transient-activation budget once the render (~2.5s
   * measured on this endpoint) and the user's network are added. `window.open`
   * then returns `null` and opens nothing WITHOUT throwing, so the catch below
   * never ran and the button silently did nothing (fe#763). Same defect, same
   * fix and same helper as the ticket document menu — see `lib/blob-tab.ts`.
   */
  const handleDownloadPdf = async () => {
    // Claimed inside the gesture, or not at all.
    const win = claimBlobTab();
    if (!win) {
      setToast(POPUP_BLOCKED_MESSAGE);
      return;
    }
    try {
      const response = await api.invoices.getPdf(invoiceId!);
      showBlobInTab(win, new Blob([response.data], { type: 'application/pdf' }));
    } catch {
      // The tab was claimed before the request; do not leave it on about:blank.
      win.close();
      setToast('Failed to download PDF');
    }
  };

  /**
   * QuickBooks Online CSV — the push's offline twin, for tenants with no QBO
   * connection (api#799). A failure is a red toast carrying the API's words,
   * never a silent no-op.
   */
  const handleDownloadCsv = async () => {
    setCsvDownloading(true);
    try {
      const filename = await downloadInvoiceCsv([invoiceId!], invoice?.invoiceNumber);
      showToast(`Downloaded ${filename}`);
    } catch (err: any) {
      showToast(err?.message || 'Failed to download the invoice CSV', 'error');
    } finally {
      setCsvDownloading(false);
    }
  };

  const handleVoid = () => {
    if (window.confirm('Are you sure you want to void this invoice? This action cannot be undone.')) {
      voidMutation.mutate();
    }
  };

  // ─── Three load states, never two ───────────────────────────────────────
  //
  // `isError || !invoice` collapsed a REFUSAL, an unreachable server and a row
  // that does not exist into one sentence — "Invoice not found" — which names
  // deletion, and is wrong for the first two. A refusal is an answer; a dropped
  // connection is not; neither is a missing row.

  if (invoiceFailure) {
    return (
      <div data-testid="invoice-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
        <InvoiceBand title={invoiceId ? `Invoice ${invoiceId.slice(0, 8)}` : 'Invoice'} onBack={() => navigate(-1)} />
        <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
          <div className="mx-auto max-w-[1920px]">
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-8">
              <LoadFailureNotice
                error={queryFailureError(invoiceQuery)}
                /* A PLURAL NOUN PHRASE: the helper renders "not a statement
                   about your {subject}". */
                subject="invoice details"
                roleLabel={user?.role ? getRoleLabel(user.role) : undefined}
                allowed="admins and office staff"
              />
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div data-testid="invoice-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
        <InvoiceBand title="Loading…" onBack={() => navigate(-1)} />
        <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
          <div className="mx-auto flex max-w-[1920px] items-center justify-center gap-2 py-20 text-fieldiq-text">
            <Loader2 size={20} className="animate-spin" /> Loading invoice…
          </div>
        </section>
      </div>
    );
  }

  // The load finished, the server answered, and it had no invoice for this id.
  // A fact about the row, not about the connection.
  if (!invoice) {
    return (
      <div data-testid="invoice-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
        <InvoiceBand title="Invoice not found" onBack={() => navigate(-1)} />
        <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
          <div className="mx-auto max-w-[1920px]">
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-8 text-center">
              <AlertTriangle size={32} className="mx-auto mb-3 text-fieldiq-muted" aria-hidden="true" />
              <p className="text-sm font-semibold text-white">There is no invoice with this id</p>
              <p className="mx-auto mt-1 max-w-sm text-xs text-fieldiq-muted">
                It may have been deleted, or the link may be wrong.
              </p>
              <button
                onClick={() => navigate('/invoices')}
                className="fi-action fi-action--standard fi-action--primary mt-4 inline-flex"
              >
                View all invoices
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const status = invoice.status;
  const lineItems = invoice.lineItems ?? [];
  const payments = invoice.payments ?? [];
  const discountTotal = invoice.discountTotal ?? 0;
  const taxLines = invoice.taxLines ?? [];
  const skin = statusSkin(status);

  return (
    <div data-testid="invoice-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <InvoiceBand
        title={invoice.invoiceNumber || `Invoice #${invoiceId?.slice(0, 8)}`}
        onBack={() => navigate(-1)}
        /* FOUR FIGURES THE INVOICE ITSELF STATES, none of them recomputed and
           none of them a second answer to a question the API already answers.
           Total, Paid and Balance come off the row through `formatMoney`, which
           is already an em dash when the key is absent — an invoice whose money
           did not arrive says so rather than reporting a $0.00 balance, which on
           an AR page reads "this is settled". Lines is the length of an array
           this screen has in hand and renders below, so it cannot disagree with
           what is on the page.

           THE THIRD LABEL IS SEVEN CHARACTERS ON PURPOSE. The hero circle's
           edge crosses the third stat column between 1024 and 1048 wide, and the
           labels swept there run to about 70px. All four were re-measured for
           this page anyway — no previous sweep covers this copy. */
        stats={[
          {
            icon: <DollarSign className="size-4" aria-hidden="true" />,
            label: 'Total',
            sub: 'Invoiced amount',
            tone: 'accent',
            value: formatMoney(invoice.total),
          },
          {
            icon: <Wallet className="size-4" aria-hidden="true" />,
            label: 'Paid',
            sub: 'Received to date',
            tone: 'success',
            value: formatMoney(invoice.amountPaid),
          },
          {
            icon: <Receipt className="size-4" aria-hidden="true" />,
            label: 'Balance',
            sub: 'Still outstanding',
            tone: 'info',
            value: formatMoney(invoice.balanceDue),
          },
          {
            icon: <FileText className="size-4" aria-hidden="true" />,
            label: 'Lines',
            sub: 'Billed items',
            tone: 'neutral',
            value: displayValue(lineItems.length),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
        {/* ─── Identity row — status, customer, due date ─────────────────
            In the body rather than on the band, the way TicketDetail's is: the
            band's only free slots are a description line and the stat strip,
            and the circle's clearance was swept against stat text alone. */}
        <div data-testid="invoice-identity-row" className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span data-testid="invoice-status-badge" className={`${STATUS_PILL} ${skin.fill}`}>
            {skin.label}
          </span>
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-white">
            <Users size={14} className="text-fieldiq-muted" aria-hidden="true" />
            {invoice.customerName || '\u2014'}
          </span>
          {/* `dueDate` is an INSTANT shown as the TENANT's day (api#838). Its
              UTC prefix reads a day late for an invoice written after 18:00 MDT. */}
          <span className="inline-flex items-center gap-1.5 text-sm text-fieldiq-text">
            <Clock size={14} className="text-fieldiq-muted" aria-hidden="true" />
            Due {formatInstantTenantDay(invoice.dueDate, timeZone) ?? '\u2014'}
          </span>
        </div>
        {suppressed && (
          <SuppressedRecipientNotice
            refusal={suppressed.refusal}
            documentLabel="invoice"
            busy={sendMutation.isPending}
            onForce={() => sendMutation.mutate({ email: suppressed.email, force: true })}
            onDismiss={() => setSuppressed(null)}
          />
        )}

        {qboRefusal && (
          <QboPushRefusalNotice
            refusal={qboRefusal}
            onOpenMapping={(kind) => navigate(qboMappingTabHref(kind))}
            onDismiss={() => setQboRefusal(null)}
          />
        )}

        {/* ─── Customer Info ───────────────────────────────────────────── */}
        <RecordPanel eyebrow="Billed to" title={invoice.customerName || '\u2014'}>
          <div className="px-4 py-3">

          {/*
            THE RECIPIENT, AND WHERE IT CAME FROM.

            The address was already rendered here and could not be changed:
            `PUT /invoices/:id` has accepted `customerContactEmail` since #514
            and nothing in this app had ever called it, so an office user
            looking at a wrong address had no way to correct it — the send
            override below only appeared when the address was ABSENT.

            The provenance line is the other half. Without it, "addressed to
            the switchboard" and "addressed to accounts payable" are the same
            line of text.
          */}
          {editingRecipient ? (
            <div className="flex items-center gap-2 mt-1.5">
              <input
                type="email"
                aria-label="Invoice recipient"
                value={recipientDraft}
                onChange={(e) => setRecipientDraft(e.target.value)}
                placeholder="Invoice recipient"
                className="bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-1.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none w-64"
                autoFocus
              />
              <button
                onClick={async () => {
                  setSavingRecipient(true);
                  try {
                    await api.invoices.update(invoiceId!, { customerContactEmail: recipientDraft.trim() || null });
                    await queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
                    setEditingRecipient(false);
                    showToast('Recipient updated');
                  } catch (err: any) {
                    // A refusal must not render as a success. The screen's
                    // toast is green with a checkmark, so an error goes to the
                    // error channel instead — see #421/#452.
                    showToast(err?.response?.data?.error || 'Failed to update recipient', 'error');
                  } finally {
                    setSavingRecipient(false);
                  }
                }}
                disabled={savingRecipient}
                aria-label="Save recipient"
                className="px-3 py-1.5 rounded-lg bg-fieldiq-accent text-fieldiq-dark text-sm font-medium disabled:opacity-50"
              >
                Save
              </button>
              <button onClick={() => setEditingRecipient(false)} className="text-fieldiq-muted hover:text-fieldiq-accent" aria-label="Cancel">
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-sm text-fieldiq-text">
                {invoice.customerContactEmail || <span className="text-fieldiq-muted">No recipient on this invoice</span>}
              </p>
              {/* PUT /invoices/:id — the same gate as Edit. */}
              {perms.canEdit && (
                <Tooltip content="Edit recipient">
                  <button
                    onClick={() => { setRecipientDraft(invoice.customerContactEmail || ''); setEditingRecipient(true); }}
                    className="text-fieldiq-muted hover:text-fieldiq-accent"
                    aria-label="Edit recipient"
                  >
                    <Pencil size={13} />
                  </button>
                </Tooltip>
              )}
            </div>
          )}

          {invoice.customerContactEmail && (
            <p className="text-xs text-fieldiq-muted mt-0.5">
              {invoice.customerEmailSource === 'accounting_email'
                ? 'Accounting address on file for this customer'
                : invoice.customerEmailSource === 'main_contact_email'
                  ? 'Main contact address on file for this customer'
                  // NEVER guess a source. An invoice created before the API
                  // recorded one has no answer, and saying either column would
                  // be inventing the very fact this line exists to carry.
                  : 'Source not recorded'}
            </p>
          )}
          </div>
        </RecordPanel>

        {/* ─── Action Buttons ──────────────────────────────────────────── */}
        {/*
          EVERY WRITE IN THIS ROW IS GATED ON ITS OWN API ROUTE'S ROLES — see
          useInvoicePermissions. Supervisors and dispatchers can read an
          invoice, and were offered every one of these doors for the API to
          refuse. Download PDF is the one read here and is shown to everyone.
        */}
        <div className="flex flex-wrap items-center gap-2">
          {(status === 'draft' || status === 'finalized') && perms.canEdit && (
            <button
              onClick={() => navigate(`/invoices/${invoiceId}/edit`)}
              className={ACTION_SECONDARY}
            >
              <Pencil size={15} />
              Edit
            </button>
          )}

          {/*
            SAVE IS THE draft -> finalized TRANSITION, AND IT IS OFFERED ONLY
            WHERE IT IS ONE.

            It used to render for `finalized` as well, in a saturated green, on
            a page that is not an editor and holds no unsaved state. On a
            finalized invoice the click PUT the status the row already had: a
            no-op that looked like the most confident control on the screen.
            Green is this app's "this is settled" hue — it belongs on the Paid
            badge, not on a button that writes nothing.

            The skin is the ordinary secondary action. There is no primary on a
            document whose one irreversible door (Void) is a refusal, and Send
            below is the action the office actually came for.
          */}
          {status === 'draft' && perms.canEdit && (
            <button
              onClick={async () => {
                try {
                  await api.invoices.update(invoiceId!, { status: 'finalized' });
                  queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
                  setToast('Invoice saved and finalized');
                } catch (err: any) {
                  showToast(err?.response?.data?.error || 'Failed to save', 'error');
                }
              }}
              className={ACTION_SECONDARY}
            >
              <CheckCircle size={15} />
              Save
            </button>
          )}

          {(status === 'draft' || status === 'finalized') && perms.canSend && (
            !showSendEmail ? (
              <button
                onClick={() => {
                  // ALWAYS open the field, whether or not the invoice carries
                  // an address.
                  //
                  // This used to send immediately when the recipient was
                  // truthy, and only prompt when it was ABSENT — so the one
                  // case the override could not reach was the case it was
                  // needed for: a present address that is WRONG. All 16 of
                  // TNT's Cenovus drafts were in exactly that state, showing
                  // info@cenovus.com on this screen with no way to change it
                  // and no way to send anywhere else.
                  //
                  // Pre-filling with the invoice's own recipient keeps the
                  // common case one extra click, and makes the address the
                  // office is about to mail VISIBLE before they mail it.
                  setSendEmailAddress(invoice.customerContactEmail || '');
                  setShowSendEmail(true);
                }}
                disabled={sendMutation.isPending}
                className={ACTION_PRIMARY}
              >
                {sendMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                Send
              </button>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="email"
                  aria-label="Invoice send address"
                  value={sendEmailAddress}
                  onChange={(e) => setSendEmailAddress(e.target.value)}
                  placeholder="Enter customer email"
                  className="w-56 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2 text-sm text-fieldiq-text placeholder-fieldiq-muted focus:border-fieldiq-accent focus:outline-none"
                  autoFocus
                />
                {/*
                  A SEND WITH NOTHING IN THE FIELD IS REFUSED HERE, WITH THE
                  REASON, RATHER THAN SWALLOWED.

                  The handler was `if (!typed) return;` — so on an invoice with
                  no recipient the button looked exactly as live as it does on
                  one with an address, and clicking it did nothing at all, with
                  no message. Nothing on screen distinguished "sent" from
                  "ignored"; the office's only signal that a send had happened
                  was the toast that never came.

                  The rule is the API's own, not one invented here:
                  `POST /invoices/:id/send` resolves the recipient as
                  `body.email || invoice.customer_contact_email` and answers 400
                  "No recipient email. Provide email in body or set customer
                  contact email." when both are empty. Empty field AND no
                  address on the document is the only state that refuses; an
                  empty field on an invoice that HAS an address is not disabled,
                  because that posts no override and the document's own address
                  is used. The remedy is one panel up — the pencil beside the
                  recipient — and the title says so.
                */}
                {(() => {
                  const typed = sendEmailAddress.trim();
                  const noRecipient = !typed && !(invoice.customerContactEmail || '').trim();
                  const reason = noRecipient
                    ? 'This invoice has no recipient. Type an address here, or set one with the pencil beside the recipient above.'
                    : undefined;
                  return (
                    <Tooltip content={reason}>
                      <button
                        onClick={() => {
                          // Post `email` ONLY when the office actually changed it.
                          //
                          // The field is pre-filled with the invoice's own address
                          // so they can see what they are about to mail. If that
                          // pre-fill were posted back as a body address, the server
                          // would treat every send as an explicit override — and a
                          // refusal would then say nothing about WHICH customer
                          // field the dead address came from, because a typed
                          // address is nobody's field. The whole provenance line
                          // would be unreachable from this screen.
                          sendMutation.mutate(typed === (invoice.customerContactEmail || '') ? {} : { email: typed });
                        }}
                        disabled={sendMutation.isPending || noRecipient}
                        aria-disabled={noRecipient}
                        className={ACTION_PRIMARY + ' disabled:opacity-50 disabled:cursor-not-allowed'}
                      >
                        {sendMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                        Send
                      </button>
                    </Tooltip>
                  );
                })()}
                <button
                  onClick={() => setShowSendEmail(false)}
                  aria-label="Cancel send"
                  className="text-fieldiq-muted transition-colors hover:text-fieldiq-accent"
                >
                  <X size={16} />
                </button>
              </div>
            )
          )}

          {/*
            PAYABLE, mirroring the API's src/lib/invoice-state.ts. Both buttons
            behind this gate accept exactly these three statuses server-side:
            POST /invoices/:id/payment locks against PAYABLE, and
            POST /invoices/:id/payment-link reaches the same set through
            stripe.service's checkout guard.

            `finalized` was missing here, and it is the status an invoice sits
            in for as long as it takes somebody to send it — so an office that
            had reconciled a payment in QuickBooks had no button at all, on an
            invoice the API would have accepted a payment for all along. It read
            as "FieldIQ can't record payments" rather than as a missing button.
          */}
          {(status === 'finalized' || status === 'sent' || status === 'overdue') && (
            <>
              {perms.canRecordPayment && (
                <button onClick={() => setShowPaymentModal(true)} className={ACTION_SECONDARY}>
                  <DollarSign size={15} />
                  Record Payment
                </button>
              )}
              {perms.canPaymentLink && (
              <button
                onClick={async () => {
                  try {
                    const res = await api.invoices.getPaymentLink(invoiceId!);
                    const url = res.data?.data?.checkoutUrl;
                    if (url) {
                      await navigator.clipboard.writeText(url);
                      setToast('Payment link copied to clipboard!');
                    } else {
                      showToast('Stripe not configured — set up in Settings > Accounting', 'error');
                    }
                  } catch (err: any) {
                    showToast(err?.response?.data?.error || 'Failed to generate payment link', 'error');
                  }
                }}
                className={ACTION_SECONDARY}
              >
                <CreditCard size={15} />
                Payment Link
              </button>
              )}
            </>
          )}

          {/* Download PDF — always available */}
          <button onClick={handleDownloadPdf} className={ACTION_SECONDARY}>
            <Download size={15} />
            Download PDF
          </button>

          {/*
            EXPORT TO ACCOUNTING — QuickBooks ONLY.

            "Push to D365" stood beside this one and is gone. Dynamics 365's
            connect flow is switched off API-side: GET /dynamics365/callback
            answers 410 for every caller (unsigned OAuth state), so no tenant
            can be connected and none ever has been.

            THE PUSH ENDPOINT ITSELF IS STILL REGISTERED AND STILL REACHABLE —
            POST /dynamics365/push-invoice/:invoiceId, admin/office_staff,
            strict. It is not a dead route, it is a live route whose first line
            is `getValidToken(companyId)`, which throws with no connection; the
            recorded API manifest has it as a 500. So the button was a control
            that could only ever fail, in a costume identical to the QBO one
            beside it that works. The CALL is gone from src/api/client.ts too
            (fe#755) — a caller-less method that looks like its working sibling
            is how it gets wired back up. Reopening the flow is an API-side
            decision about the OAuth nonce, not a frontend one, and the call
            comes back with that decision.
          */}
          {/*
            POST /quickbooks/push-invoice/:id is requireRole('admin',
            'office_staff', { strict: true }), so a supervisor or dispatcher
            who can open this invoice was offered a push the API refuses —
            see canPushQbo, the same gate the /finance/invoices row uses.
          */}
          {/*
            ONCE IN QUICKBOOKS, NO PUSH. The API refuses a second push
            (QBO_ALREADY_PUSHED), so the button must not invite it.

            OTHERWISE DISABLED UNTIL THE COMPANY IS READY — including while
            readiness loads (qboPushGate). The reason is TEXT beside the
            button, because a tooltip is invisible on touch.
          */}
          {status !== 'void' && perms.canPushQbo && (invoice.qboInvoiceId || pushedQboId) ? (
            <span data-testid="qbo-pushed-badge" className="inline-flex items-center gap-1 rounded-fi-panel bg-fi-signal-green px-2.5 py-1 text-sm font-semibold text-fi-signal-ink">
              ✓ In QuickBooks #{invoice.qboInvoiceId || pushedQboId}
            </span>
          ) : status !== 'void' && perms.canPushQbo && (
            <span className="inline-flex flex-wrap items-center gap-2">
              <button
                onClick={() => qboPushMutation.mutate()}
                disabled={qboPushMutation.isPending || !pushGate.enabled}
                className={ACTION_SECONDARY}
              >
                {qboPushMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : <ExternalLink size={15} />}
                Push to QBO
              </button>
              <QboPushOffReason gate={pushGate} />
            </span>
          )}

          {/*
            The same invoice as a QuickBooks import file. Void is excluded
            server-side, so the button is too. Shown to the push's roles —
            see canExportCsv.
          */}
          {status !== 'void' && perms.canExportCsv && (
            <button
              onClick={handleDownloadCsv}
              disabled={csvDownloading}
              className={ACTION_SECONDARY}
            >
              {csvDownloading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
              Download CSV
            </button>
          )}

          {/* Duplicate */}
          {perms.canDuplicate && (
          <button
            onClick={async () => {
              try {
                const res = await api.invoices.duplicate(invoiceId!);
                const newId = res.data?.data?.id;
                setToast('Invoice duplicated');
                if (newId) navigate(`/invoices/${newId}`);
              } catch { showToast('Failed to duplicate', 'error'); }
            }}
            className={ACTION_SECONDARY}
          >
            <FileText size={15} />
            Duplicate
          </button>
          )}

          {/* Credit Note */}
          {(status === 'sent' || status === 'overdue' || status === 'paid') && perms.canCreditNote && (
            <button
              onClick={() => {
                const amount = prompt('Credit amount ($):');
                const reason = prompt('Reason for credit:');
                if (amount && reason) {
                  api.invoices.creditNote(invoiceId!, { amount: parseFloat(amount), reason })
                    .then(() => {
                      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
                      setToast('Credit note created');
                    })
                    .catch(() => showToast('Failed to create credit note', 'error'));
                }
              }}
              className={ACTION_SECONDARY}
            >
              <CreditCard size={15} />
              Credit Note
            </button>
          )}

          {/*
            VOID GOES THROUGH THE DEDICATED DOOR, and this is the one place it
            can. `api.invoices.void` is POST /invoices/:id/void — the state
            machine that refuses an already-paid invoice, reverts
            ticket_line_items.invoiced, walks invoice_tickets back to
            office_approved, drops the line-item snapshots so the tickets can be
            re-invoiced, stamps voided_at and writes the audit row. Verified
            against src/api/client.ts, not assumed.

            There is no second path from this screen, and there must not be:
            api#789 closed PATCH /unified-invoices/:id against `status: 'void'`
            precisely because six characters into a column leaves every ticket
            sitting in `invoiced` while the row reads voided everywhere.
          */}
          {status !== 'void' && perms.canVoid && (
            <button
              onClick={handleVoid}
              disabled={voidMutation.isPending}
              className={ACTION_DANGER + ' ml-auto'}
            >
              {voidMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : <Ban size={15} />}
              Void
            </button>
          )}
        </div>

        {/* ─── Linked Tickets ─────────────────────────────────────────── */}
        {invoice.tickets && invoice.tickets.length > 0 && (
          <RecordPanel eyebrow="Source documents" title="Linked field tickets" count={invoice.tickets.length}>
            <div className="space-y-2 px-4 py-3">
              {invoice.tickets.map((t: any) => (
                <div key={t.id} className="flex items-center justify-between bg-fieldiq-dark rounded-lg px-4 py-2.5">
                  <div className="flex items-center gap-3">
                    <span className="text-fieldiq-accent font-medium text-sm">{t.ticketNumber || t.ticket_number}</span>
                    {/* The API's ticket date (api#1329): the finish day when the
                        company dates tickets by it, else work_date — and the
                        order these arrive in. Read, never derived. */}
                    {readTicketDate(t) && (
                      <span className="text-xs text-fieldiq-muted">{formatCalendarDate(readTicketDate(t)!, { month: 'short', day: 'numeric' }, 'en-US') ?? '\u2014'}</span>
                    )}
                    {(t.rigName || t.rig_name) && <span className="text-xs text-fieldiq-muted">· {t.rigName || t.rig_name}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={async () => {
                        // Tickets reach an invoice through customer approval, so
                        // in practice every one of these is a status the API will
                        // render for real. This payload does not promise a
                        // `status` field though, and `ticketPdfNeedsPreview`
                        // answers false when it is absent — so an unknown status
                        // asks for the customer document and lets the 409 speak
                        // rather than silently downloading a watermarked page.
                        const preview = ticketPdfNeedsPreview(t.status ?? t.ticket_status);
                        try {
                          const res = await api.tickets.getPdf(t.id, { preview });
                          // Revoked on a timer inside the helper, never on the
                          // next statement: a.click() only schedules the download.
                          saveBlobAs(
                            new Blob([res.data]),
                            `${t.ticketNumber || t.ticket_number}${preview ? '-PREVIEW' : ''}.pdf`
                          );
                        } catch (err) {
                          setToast(await getBlobApiErrorMessage(err, 'Failed to download ticket PDF'));
                        }
                      }}
                      className="text-xs text-fieldiq-accent hover:text-fieldiq-accent/80 flex items-center gap-1"
                    >
                      <Download size={12} /> Ticket PDF
                    </button>
                    <button
                      onClick={() => navigate(`/tickets/${t.id}`)}
                      className="text-xs text-fieldiq-text hover:text-fieldiq-accent flex items-center gap-1"
                    >
                      View →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </RecordPanel>
        )}

        {/* ─── Line Items Table ────────────────────────────────────────── */}
        <RecordPanel eyebrow="What was billed" title="Line items" count={lineItems.length}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead>
                <tr className="border-b border-fieldiq-border text-fieldiq-text text-left">
                  <th className="px-4 py-2 font-medium">Ticket#</th>
                  <th className="px-4 py-2 font-medium">Date</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Description</th>
                  <th className="px-4 py-2 font-medium text-right">Qty</th>
                  <th className="px-4 py-2 font-medium">Unit</th>
                  <th className="px-4 py-2 font-medium text-right">Rate</th>
                  <th className="px-4 py-2 font-medium text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-6 text-center text-fieldiq-text">
                      No line items
                    </td>
                  </tr>
                ) : (
                  lineItems.map((item) => (
                    /* A DISCOUNT ROW IS ITALIC, NOT RED. The 400-weight red it
                       carried is a raw shade with no light twin — on the white
                       card it reads around 2.5:1, so on half the installs the
                       discount lines were the least readable rows on an invoice.
                       The italic and the leading minus on the amount carry it. */
                    <tr
                      key={item.id}
                      className={`border-b border-fieldiq-border/50 ${
                        item.isDiscount ? 'italic text-fieldiq-text' : 'text-white'
                      }`}
                    >
                      {/* EVERY ABSENT CELL ON THIS ROW IS AN EM DASH.
                          They were the two-hyphen string `--`, which is the
                          repo's own written exception on Quotes, not a pattern:
                          a manual line carries no ticket and no date, so those
                          two cells render absent on EVERY hand-added line — the
                          commonest row on a hand-built invoice was also the one
                          spelling the placeholder wrong. */}
                      <td className="px-4 py-2 font-medium text-fieldiq-accent">{item.ticketNumber || '\u2014'}</td>
                      <td className="px-4 py-2">
                        {formatCalendarDate(item.date ? String(item.date) : null, { month: 'short', day: 'numeric' }, 'en-US') ?? '\u2014'}
                      </td>
                      {/* THE SHARED LABEL HELPERS, not a local capitalize.
                          `capitalize` on `crt` renders "Crt"; the invoice PDF
                          this screen downloads renders "CRT", because the API's
                          formatLineItemType upper-cases a short code. One
                          document, two spellings, and the one the customer gets
                          is the PDF. `fmtLineItemType` is the frontend mirror of
                          that same function and agrees with it. */}
                      <td className="px-4 py-2">{item.type ? fmtLineItemType(item.type) : '\u2014'}</td>
                      <td className="px-4 py-2 max-w-[200px] truncate">{item.description || '\u2014'}</td>
                      <td className="px-4 py-2 text-right">{item.quantity}</td>
                      <td className="px-4 py-2">{item.unit ? fmtUnit(item.unit) : '\u2014'}</td>
                      <td className="px-4 py-2 text-right">{formatRate(item.rate)}</td>
                      <td className="px-4 py-2 text-right font-medium">
                        {item.isDiscount ? formatMoneyWithSign(item.amount) : formatMoney(item.amount)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ─── Totals footer ──────────────────────────────────────────── */}
          <div className="border-t border-fieldiq-border px-4 py-3 space-y-1">
            <div className="flex justify-between text-sm text-fieldiq-text">
              <span>Subtotal</span>
              <span className="text-white">{formatMoney(invoice.subtotal)}</span>
            </div>

            {discountTotal !== 0 && (
              <div className="flex justify-between text-sm italic text-fieldiq-text">
                <span>Discounts</span>
                <span>-{formatMoney(Math.abs(discountTotal))}</span>
              </div>
            )}

            {taxLines.map((tax, i) => (
              <div key={i} className="flex justify-between text-sm text-fieldiq-text">
                <span>
                  {tax.label} ({tax.rate}%)
                </span>
                <span className="text-white">{formatMoney(tax.amount)}</span>
              </div>
            ))}

            {invoice.taxTotal != null && taxLines.length === 0 && invoice.taxTotal !== 0 && (
              <div className="flex justify-between text-sm text-fieldiq-text">
                <span>Tax</span>
                <span className="text-white">{formatMoney(invoice.taxTotal)}</span>
              </div>
            )}

            {/*
              THE THREE FIGURES THAT DECIDE THE DOCUMENT, IN INK TOKENS.

              Amount Paid and Balance Due were raw 400-weight green and amber,
              which theme.css remaps not at all: on the white card they measure
              about 1.5 and 2.0. The two lines most likely to be read across a
              desk were the two least legible ones, on exactly half the installs.

              ALL THREE ARE PLAIN HEADING INK NOW, and none of them carries a
              hue. Weight and the rule above Total are the hierarchy. Balance Due
              in particular had a green/amber SWITCH on `> 0`, which is a second,
              quieter channel restating the status badge two panels up — and it
              said the same thing about a settled $0.00 and about an ABSENT
              balance, because both are "not greater than zero". Amber on this
              register means the office must act; a paid invoice's balance is not
              a thing to act on, and an em dash is not one either.
            */}
            <div className="flex justify-between border-t border-fieldiq-border/50 pt-1 text-base font-semibold text-white">
              <span>Total</span>
              <span>{formatMoney(invoice.total)}</span>
            </div>

            <div className="flex justify-between text-sm text-fieldiq-text">
              <span>Amount Paid</span>
              <span className="text-white">{formatMoney(invoice.amountPaid)}</span>
            </div>

            <div className="flex justify-between text-base font-semibold text-white">
              <span>Balance Due</span>
              <span>{formatMoney(invoice.balanceDue)}</span>
            </div>
          </div>
        </RecordPanel>

        {/* ─── Payments ────────────────────────────────────────────────── */}
        {payments.length > 0 && (
          <RecordPanel eyebrow="Cash received" title="Payments" count={payments.length}>
            <div className="space-y-2 px-4 py-3">
              {payments.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between bg-fieldiq-dark/50 rounded-lg px-3 py-2 text-sm"
                >
                  <div>
                    <span className="text-white font-medium">
                      {/* payment_date (or created_at) — an instant, shown as the tenant's day */}
                      {formatInstantTenantDay(p.date, timeZone) ?? '—'}
                    </span>
                    {p.method && (
                      <span className="text-fieldiq-text ml-2 capitalize">
                        {p.method.replace(/_/g, ' ')}
                      </span>
                    )}
                    {p.reference && (
                      <span className="text-fieldiq-text ml-2">
                        Ref: {p.reference}
                      </span>
                    )}
                  </div>
                  {/* Plain heading ink. The 400-weight green this carried is
                      remapped by nothing and reads about 1.5:1 on the light
                      card — a payment amount nobody can read. */}
                  <span className="font-medium text-white">{formatMoney(p.amount)}</span>
                </div>
              ))}
            </div>
          </RecordPanel>
        )}

        {/* ─── Audit Log (collapsible) ─────────────────────────────────── */}
        <section className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card">
          <button
            onClick={() => setAuditOpen(!auditOpen)}
            aria-expanded={auditOpen}
            aria-controls="invoice-audit-log"
            className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:text-fieldiq-accent"
          >
            <span>
              <span className="block text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">
                Who changed what
              </span>
              <span className="mt-1 flex items-center gap-2 text-base font-extrabold tracking-tight text-white">
                <Clock size={15} className="text-fieldiq-muted" aria-hidden="true" />
                Audit log
              </span>
            </span>
            {auditOpen ? <ChevronUp size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
          </button>

          {auditOpen && (
            <div
              id="invoice-audit-log"
              /* NAMED, so a reader on a screen reader lands somewhere and a
                 test can ask for it by role instead of reaching for it by id.
                 The `aria-controls` above needs the id; the region needs the
                 name. */
              role="region"
              aria-label="Audit log entries"
              className="space-y-2 border-t border-fieldiq-border px-4 py-3"
            >
              {auditLoading ? (
                <div className="flex items-center gap-2 text-fieldiq-text text-sm py-4 justify-center">
                  <Loader2 size={16} className="animate-spin" />
                  Loading audit log...
                </div>
              ) : !auditLog || auditLog.length === 0 ? (
                <p className="text-sm text-fieldiq-text text-center py-4">No audit entries</p>
              ) : (
                auditLog.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-start gap-3 text-sm bg-fieldiq-dark/50 rounded-lg px-3 py-2"
                  >
                    <User size={14} className="text-fieldiq-text mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        {/* The raw column value read `manual_create` on screen. */}
                        <span className="font-medium text-white">{humanizeLabel(entry.action)}</span>
                        {auditActor(entry) && (
                          <span className="text-fieldiq-text">by {auditActor(entry)}</span>
                        )}
                      </div>
                      {entry.description && (
                        <p className="mt-0.5 text-fieldiq-text">{entry.description}</p>
                      )}
                      {/* An unparseable or absent stamp is an em dash, not the
                          words "Invalid Date" — which is what this line printed
                          on every row, because it read a key the payload has
                          never carried. */}
                      <p className="mt-0.5 text-xs text-fieldiq-muted">
                        {formatInstant(entry.created_at ?? entry.createdAt, timeZone, {
                          month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
                        }, 'en-US') ?? '\u2014'}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </section>
        </div>
      </section>

      {/* ─── Payment Modal ───────────────────────────────────────────── */}
      {showPaymentModal && (
        <RecordPaymentModal
          invoiceId={invoiceId!}
          balanceDue={invoice.balanceDue}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={() => setToast('Payment recorded successfully')}
          onError={(msg) => setToast(msg)}
        />
      )}

      {/* ─── Toast ───────────────────────────────────────────────────── */}
      {toast && <Toast message={toast} tone={toastTone} onClose={() => setToast(null)} />}
    </div>
  );
}
